import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import { Settings, Save } from 'lucide-react';

const AdminMLMSettings = () => {
  const [settings, setSettings] = useState({ commission_rates: [], referral_reward: {} });
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [settingsRes, rewardsRes] = await Promise.all([
        api.get('/mlm/settings'),
        api.get('/mlm/rewards-table'),
      ]);
      setSettings(settingsRes.data);
      setRewards(rewardsRes.data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-heading font-bold text-gray-900">MLM Settings</h1>
          <p className="text-gray-600 mt-1">Manage commission rates and referral rewards</p>
        </div>

        {/* Commission Rates */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">10-Level Commission Rates</h2>
          <div className="grid grid-cols-5 gap-4">
            {settings.commission_rates.map((rate, idx) => (
              <div key={idx} className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-600 mb-1">Level {idx + 1}</p>
                <p className="text-lg font-bold text-primary">{rate}%</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-600 mt-4">
            Current rates: L1: 1.0% → L10: 0.1% (decreasing by 0.1% per level)
          </p>
        </div>

        {/* Referral Rewards */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Direct Referral Rewards</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-primary-light rounded-lg p-4">
              <p className="text-sm text-gray-700 mb-2">Prime Plan Referral</p>
              <p className="text-3xl font-bold text-primary">${settings.referral_reward.prime}</p>
              <p className="text-xs text-gray-600 mt-1">One-time reward per referral</p>
            </div>
            <div className="bg-success-light rounded-lg p-4">
              <p className="text-sm text-gray-700 mb-2">Premium Plan Referral</p>
              <p className="text-3xl font-bold text-success">${settings.referral_reward.premium}</p>
              <p className="text-xs text-gray-600 mt-1">One-time reward per referral</p>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <input
              type="checkbox"
              checked={settings.referral_reward.requireKycApproved}
              disabled
              className="w-4 h-4 text-primary"
            />
            <label className="text-sm text-gray-700">Require KYC Approved before paying reward</label>
          </div>
        </div>

        {/* Milestone Rewards Table */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Milestone Rewards Configuration</h2>
          <div className="space-y-3">
            {rewards.map((reward) => (
              <div key={reward.level} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <img
                  src={reward.imageUrl}
                  alt={reward.physicalReward}
                  className="w-16 h-16 object-cover rounded-lg"
                />
                <div className="flex-1 grid grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-gray-600">Level</p>
                    <p className="font-bold text-gray-900">L{reward.level} - {reward.title}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Profit Share</p>
                    <p className="font-semibold text-primary">{reward.profitSharePercent}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Cash Reward</p>
                    <p className="font-semibold text-success">${reward.cashRewardUsd.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Physical Reward</p>
                    <p className="text-sm text-gray-900">{reward.physicalReward}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminMLMSettings;